import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Header, Segment, Message, Icon, Modal } from 'semantic-ui-react';
import { fetchTenantApiToken, regenerateTenantApiToken } from '../../../actions/externalApiActions';
import { toast } from 'react-toastify';
import _ from 'lodash';

const TenantApiTokenPage = () => {
    const dispatch = useDispatch();
    const { tenantApiToken, tenantTokenCreatedAt, loading } = useSelector(state => state.externalApi);
    const activeTenant = useSelector(state => state.tenantManagement.activeTenant);
    const userPermissions = useSelector(state => _.isEmpty(state.authUserRoles.permissions) ? {} : state.authUserRoles.permissions);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const hasPermissions = (category) => !_.isEmpty(userPermissions) && userPermissions.hasOwnProperty(category);
    const isAdmin = () => hasPermissions('Other') && userPermissions['Other'].actions.admin;
    const canReadExternalApi = () => hasPermissions('External API') && userPermissions['External API'].actions.READ || isAdmin();
    const canUpdateExternalApi = () => hasPermissions('External API') && userPermissions['External API'].actions.UPDATE || isAdmin();

    useEffect(() => {
        if (activeTenant && activeTenant.name) {
            if (canReadExternalApi()) {
                dispatch(fetchTenantApiToken());
            }
        }
    }, [dispatch, activeTenant]);

    const handleCopyToken = () => {
        navigator.clipboard.writeText(tenantApiToken);
        toast.success('Token copied to clipboard');
    };

    const handleRegenerateToken = async () => {
        try {
            await dispatch(regenerateTenantApiToken());
            toast.success('Token regenerated successfully');
            setShowConfirmModal(false);
        } catch (error) {
            toast.error('Failed to regenerate token');
        }
    };

    return (
        <Container style={{ marginTop: '2rem' }}>
            {!canReadExternalApi() ? (
                <Message error>
                    <Message.Header>Access Denied</Message.Header>
                    <p>You do not have permission to view External API settings.</p>
                </Message>
            ) : (
            <>
            <Header as="h2">
                <Icon name="key" />
                Tenant API Token
            </Header>

            <Segment loading={loading}>
                <Header as="h3">Your API Token</Header>
                <p>Use this token to authenticate external API requests for your questionnaires.</p>

                {tenantApiToken && (
                    <>
                        <Segment secondary style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <code style={{ fontSize: '14px', wordBreak: 'break-all', flex: 1 }}>
                                {tenantApiToken}
                            </code>
                            <Button 
                                icon="copy" 
                                primary 
                                onClick={handleCopyToken}
                                style={{ marginLeft: '1rem' }}
                            />
                        </Segment>

                        {tenantTokenCreatedAt && (
                            <p style={{ color: '#666', fontSize: '12px' }}>
                                Generated: {new Date(tenantTokenCreatedAt).toLocaleString()}
                            </p>
                        )}

                        {canUpdateExternalApi() && (
                            <Button 
                                color="orange" 
                                onClick={() => setShowConfirmModal(true)}
                                style={{ marginTop: '1rem' }}
                            >
                                <Icon name="refresh" />
                                Regenerate Token
                            </Button>
                        )}
                    </>
                )}

                <Message warning icon style={{ marginTop: '2rem' }}>
                    <Icon name="warning sign" />
                    <Message.Content>
                        <Message.Header>Keep this token secure</Message.Header>
                        This token provides access to your questionnaire data. Do not share it publicly.
                        Regenerating the token will invalidate the old one.
                    </Message.Content>
                </Message>
            </Segment>

            <Modal
                open={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                size="small"
            >
                <Modal.Header>Confirm Token Regeneration</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to regenerate your API token?</p>
                    <p><strong>This will invalidate your current token and any integrations using it will stop working.</strong></p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button color="orange" onClick={handleRegenerateToken}>
                        <Icon name="refresh" />
                        Regenerate
                    </Button>
                </Modal.Actions>
            </Modal>
            </>
            )}
        </Container>
    );
};

export default TenantApiTokenPage;
